import { apiFetch } from './client';

export type UnitKind = 'per_account' | 'per_channel' | 'per_gb' | 'per_did' | 'flat_monthly' | 'per_event' | 'minimum_floor';
export type BillableScope = 'owner_to_reseller' | 'reseller_to_customer';

export interface BillableItemRow {
  id: string;
  code: string;
  name: string;
  description: string | null;
  unit_kind: UnitKind;
  default_unit_price_cents: number;
  scope: BillableScope;
  available_to_reseller: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PricingRuleRow {
  id: string;
  billable_item_id: string;
  reseller_id: string | null;
  customer_id: string | null;
  unit_price_cents: number;
  minimum_units: number;
  enabled: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
  item_code: string | null;
  item_name: string | null;
  item_unit_kind: UnitKind | null;
  item_scope: BillableScope | null;
}

export type BillableItemBody = Partial<{
  code: string;
  name: string;
  description: string | null;
  unit_kind: UnitKind;
  default_unit_price_cents: number;
  scope: BillableScope;
  available_to_reseller: boolean;
  active: boolean;
}>;

export interface UpsertRuleBody {
  billable_item_id: string;
  reseller_id?: string | null;
  customer_id?: string | null;
  unit_price_cents: number;
  minimum_units?: number;
  enabled?: boolean;
  notes?: string | null;
}

// ── Custom invoices (reseller → customer, issued by the custom-billing
//    engine — distinct from the Stripe subscription invoices in billing.ts) ──

export type CustomInvoiceStatus = 'draft' | 'issued' | 'paid' | 'failed' | 'void';

export interface CustomInvoiceRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  number: string;
  /** YYYY-MM-DD date strings (Postgres `date`). */
  period_start: string;
  period_end: string;
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  status: CustomInvoiceStatus;
  issued_at: string | null;
  paid_at: string | null;
  due_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  /** LEFT JOINed from customers — null if RLS hides the row. */
  customer_name: string | null;
  customer_number: number | null;
}

export interface CustomInvoiceItemRow {
  id: string;
  description: string;
  /** numeric(14,4) — pg returns it as a string. */
  quantity: string;
  unit_price_cents: number;
  amount_cents: number;
  meta: Record<string, unknown>;
}

export interface ChargeInvoiceResult {
  success: boolean;
  status: 'paid' | 'failed';
  gatewayRef: string | null;
  errorCode: string | null;
  errorMessage: string | null;
}

export const customBilling = {
  listItems: (p: { scope?: BillableScope; active_only?: boolean; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (p.scope) q.set('scope', p.scope);
    if (p.active_only) q.set('active_only', 'true');
    if (p.limit) q.set('limit', String(p.limit));
    return apiFetch<{ rows: BillableItemRow[] }>(`/api/billable-items?${q}`);
  },
  createItem: (body: BillableItemBody) =>
    apiFetch<BillableItemRow>('/api/billable-items', { method: 'POST', body: JSON.stringify(body) }),
  updateItem: (id: string, body: BillableItemBody) =>
    apiFetch<BillableItemRow>(`/api/billable-items/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteItem: (id: string) =>
    apiFetch<void>(`/api/billable-items/${id}`, { method: 'DELETE' }),

  listRules: (p: { reseller_id?: string; customer_id?: string; billable_item_id?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(p)) if (v !== undefined) q.set(k, String(v));
    return apiFetch<{ rows: PricingRuleRow[] }>(`/api/pricing-rules?${q}`);
  },
  upsertRule: (body: UpsertRuleBody) =>
    apiFetch<PricingRuleRow>('/api/pricing-rules', { method: 'PUT', body: JSON.stringify(body) }),
  deleteRule: (id: string) =>
    apiFetch<void>(`/api/pricing-rules/${id}`, { method: 'DELETE' }),

  listInvoices: (p: { customer_id?: string; status?: CustomInvoiceStatus; limit?: number } = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(p)) if (v !== undefined && v !== '') q.set(k, String(v));
    return apiFetch<{ rows: CustomInvoiceRow[] }>(`/api/custom-invoices?${q}`);
  },
  getInvoice: (id: string) =>
    apiFetch<{ invoice: CustomInvoiceRow; items: CustomInvoiceItemRow[] }>(`/api/custom-invoices/${id}`),
  /** Super-admin only — runs the invoice through the customer's default
   *  gateway. Declines come back as HTTP 402, which apiFetch throws; the
   *  ChargeInvoiceResult body is on the thrown ApiError's `details`. */
  chargeInvoice: (id: string) =>
    apiFetch<ChargeInvoiceResult>(`/api/custom-invoices/${id}/charge`, { method: 'POST', body: '{}' }),
};

export const UNIT_KIND_LABELS: Record<UnitKind, string> = {
  per_account: 'Per customer',
  per_channel: 'Per channel',
  per_gb: 'Per GB',
  per_did: 'Per DID',
  flat_monthly: 'Flat monthly',
  per_event: 'Per event',
  minimum_floor: 'Minimum floor',
};

export const SCOPE_LABELS: Record<BillableScope, string> = {
  owner_to_reseller: 'Owner → Reseller',
  reseller_to_customer: 'Reseller → Customer',
};
